// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * PetAlertMuteSwitch — "email me alerts for this patient" for the signed-in
 * vet's OWN account only (vic/vetpo#70). Sits beside the assignees on PetShow.
 *
 * GET/PUT /api/pets/:id/alert-mute → { muted, alertsSilenced }. The server
 * scopes it to the caller; nobody else's alerts change. `alertsSilenced` means
 * every recipient muted the patient or has alerts off, so NOBODY is emailed —
 * that is shown as a warning, same consequence as the assign dialog's (R26).
 * The switch is ON when alerts are on, i.e. `!muted`.
 */
import { Alert, FormControlLabel, Switch } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

interface MuteState {
  muted: boolean
  alertsSilenced: boolean
}

/** Rendered by the caller for vet roles only (the endpoint 403s anyone else). */
const PetAlertMuteSwitch: React.FC<{ petId: number | null }> = ({ petId }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [state, setState] = useState<MuteState | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // State is per patient: drop the previous patient's switch before loading.
    setState(null)
    if (petId == null) return
    const ctrl = new AbortController()
    ;(async () => {
      try {
        const res = await deadlineFetch(`/api/pets/${petId}/alert-mute`, {
          headers: authHeaders(),
          signal: ctrl.signal,
        })
        if (!res.ok) return // no switch rather than a wrong one
        const data = await res.json()
        if (!ctrl.signal.aborted) {
          setState({ muted: !!data?.muted, alertsSilenced: !!data?.alertsSilenced })
        }
      } catch {
        /* no switch */
      }
    })()
    return () => ctrl.abort()
  }, [petId])

  if (petId == null || !state) return null

  const onToggle = async (alertsOn: boolean) => {
    setSaving(true)
    try {
      const res = await deadlineFetch(`/api/pets/${petId}/alert-mute`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ muted: !alertsOn }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(String(res.status))
      setState({ muted: !!data?.muted, alertsSilenced: !!data?.alertsSilenced })
    } catch {
      notify('pos.clinic_team.mute_error', { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <FormControlLabel
        sx={{ m: 0 }}
        control={
          <Switch
            size="small"
            checked={!state.muted}
            disabled={saving}
            onChange={(e) => onToggle(e.target.checked)}
            inputProps={{ 'data-testid': 'pet-alert-mute-switch' } as any}
          />
        }
        label={translate('pos.clinic_team.mute_label')}
      />
      {state.alertsSilenced && (
        <Alert severity="warning" data-testid="pet-alerts-silenced-warning">
          {translate('pos.clinic_team.mute_silenced_warning')}
        </Alert>
      )}
    </>
  )
}

export default PetAlertMuteSwitch
